import { z } from "zod";

import { extensionInstallIdSchema, extensionVersionSchema } from "./extension-claim";

/**
 * Shared operation bound for validation, server policy, and Jobs selection.
 * Increasing it raises transaction size and tailoring fan-out; decreasing it
 * rejects selections that previously fit.
 */
export const JOB_BULK_SELECTION_MAX = 25;
const nullableIdSchema = z.string().min(1).nullable();
const nullableTextSchema = z.string().nullable();
const orderedJobIdsSchema = z
  .array(z.string().min(1))
  .min(1)
  .max(JOB_BULK_SELECTION_MAX)
  .refine((ids) => new Set(ids).size === ids.length, "jobIds must be unique");

export const bulkActionSchema = z.enum(["save", "hide", "tailor_for_review", "queue_easy_apply"]);
export const quotaSpendingBulkActionSchema = z.enum(["tailor_for_review", "queue_easy_apply"]);
export const bulkItemClassificationSchema = z.enum([
  "eligible",
  "already_processed",
  "unsupported",
  "stale",
  "blocked",
]);
export const bulkItemOutcomeSchema = z.enum([
  "accepted",
  "already_exists",
  "unsupported",
  "stale",
  "allowance_unavailable",
  "failed",
]);

export const bulkPreflightRequestSchema = z
  .object({
    action: quotaSpendingBulkActionSchema,
    jobProfileId: z.string().min(1),
    jobIds: orderedJobIdsSchema,
  })
  .strict();

export const bulkCommitRequestSchema = z
  .object({
    idempotencyKey: z.string().uuid(),
    action: bulkActionSchema,
    jobProfileId: z.string().min(1),
    jobIds: orderedJobIdsSchema,
  })
  .strict();

const bulkItemBaseSchema = z.object({
  jobId: z.string().min(1),
  ordinal: z
    .number()
    .int()
    .min(0)
    .max(JOB_BULK_SELECTION_MAX - 1),
  title: z.string(),
  company: z.string(),
  reasonCode: nullableTextSchema,
  applicationId: nullableIdSchema,
  applicationStatus: nullableTextSchema,
  applicationJobProfileId: nullableIdSchema,
});

export const bulkPreflightItemSchema = bulkItemBaseSchema
  .extend({ classification: bulkItemClassificationSchema })
  .strict();

export const bulkResultItemSchema = bulkItemBaseSchema
  .extend({
    outcome: bulkItemOutcomeSchema,
    tailoringAllowanceConsumed: z.union([z.literal(0), z.literal(1)]),
  })
  .strict();

export const bulkTotalsSchema = z
  .object({
    selected: z.number().int().min(1).max(JOB_BULK_SELECTION_MAX),
    eligible: z.number().int().min(0).max(JOB_BULK_SELECTION_MAX),
    accepted: z.number().int().min(0).max(JOB_BULK_SELECTION_MAX),
    alreadyExists: z.number().int().min(0).max(JOB_BULK_SELECTION_MAX),
    unsupported: z.number().int().min(0).max(JOB_BULK_SELECTION_MAX),
    stale: z.number().int().min(0).max(JOB_BULK_SELECTION_MAX),
    allowanceUnavailable: z.number().int().min(0).max(JOB_BULK_SELECTION_MAX),
    failed: z.number().int().min(0).max(JOB_BULK_SELECTION_MAX),
  })
  .strict();

export const allowancePreviewSchema = z
  .object({
    tailoringMayConsume: z.number().int().min(0).max(JOB_BULK_SELECTION_MAX),
    applicationMayConsume: z.number().int().min(0).max(JOB_BULK_SELECTION_MAX),
    tailoringRemaining: z.number().int().min(-1),
    applicationRemaining: z.number().int().min(-1),
  })
  .strict();

export const bulkPreflightResponseSchema = z
  .object({
    action: quotaSpendingBulkActionSchema,
    totals: bulkTotalsSchema,
    allowance: allowancePreviewSchema,
    items: z.array(bulkPreflightItemSchema).min(1).max(JOB_BULK_SELECTION_MAX),
    evaluatedAt: z.string().datetime(),
  })
  .strict();

export const applyBatchStatusSchema = z.enum([
  "preparing",
  "ready",
  "running",
  "stopping",
  "paused",
  "blocked",
  "completed",
  "cancelled",
]);

export const applyBatchItemStatusSchema = z.enum([
  "rejected",
  "preparing",
  "ready",
  "claimed",
  "applied",
  "skipped",
  "attention_required",
  "failed",
]);

export const batchCountsSchema = z
  .object({
    selected: z.number().int().min(0).max(JOB_BULK_SELECTION_MAX),
    accepted: z.number().int().min(0).max(JOB_BULK_SELECTION_MAX),
    preparing: z.number().int().min(0).max(JOB_BULK_SELECTION_MAX),
    ready: z.number().int().min(0).max(JOB_BULK_SELECTION_MAX),
    inProgress: z.number().int().min(0).max(1),
    applied: z.number().int().min(0).max(JOB_BULK_SELECTION_MAX),
    skipped: z.number().int().min(0).max(JOB_BULK_SELECTION_MAX),
    attentionRequired: z.number().int().min(0).max(JOB_BULK_SELECTION_MAX),
    unsupported: z.number().int().min(0).max(JOB_BULK_SELECTION_MAX),
    duplicate: z.number().int().min(0).max(JOB_BULK_SELECTION_MAX),
    stale: z.number().int().min(0).max(JOB_BULK_SELECTION_MAX),
    allowanceUnavailable: z.number().int().min(0).max(JOB_BULK_SELECTION_MAX),
    failed: z.number().int().min(0).max(JOB_BULK_SELECTION_MAX),
    remaining: z.number().int().min(0).max(JOB_BULK_SELECTION_MAX),
  })
  .strict();

export const batchSummarySchema = z
  .object({
    id: z.string().min(1),
    jobProfileId: nullableIdSchema,
    profileName: z.string(),
    status: applyBatchStatusSchema,
    counts: batchCountsSchema,
    blockedReasonCode: nullableTextSchema,
    createdAt: z.string().datetime(),
    canStart: z.boolean(),
  })
  .strict();

export const batchDetailItemSchema = z
  .object({
    selectedJobId: z.string().min(1),
    jobId: nullableIdSchema,
    ordinal: z
      .number()
      .int()
      .min(0)
      .max(JOB_BULK_SELECTION_MAX - 1),
    title: z.string(),
    company: z.string(),
    status: applyBatchItemStatusSchema,
    reasonCode: nullableTextSchema,
    applicationId: nullableIdSchema,
  })
  .strict();

export const batchDetailSchema = batchSummarySchema
  .extend({ items: z.array(batchDetailItemSchema).max(JOB_BULK_SELECTION_MAX) })
  .strict();

export const bulkCommitResponseSchema = z
  .object({
    operationId: z.string().min(1),
    idempotencyKey: z.string().uuid(),
    action: bulkActionSchema,
    replayed: z.boolean(),
    totals: bulkTotalsSchema,
    items: z.array(bulkResultItemSchema).min(1).max(JOB_BULK_SELECTION_MAX),
    batch: batchSummarySchema.nullable(),
  })
  .strict();

export const listBatchesResponseSchema = z
  .object({
    items: z.array(batchSummarySchema),
    nextCursor: z.string().nullable(),
  })
  .strict();

export const listExtensionBatchesRequestSchema = z
  .object({ installId: extensionInstallIdSchema })
  .strict();
export const listExtensionBatchesResponseSchema = z
  .object({ items: z.array(batchSummarySchema).max(20) })
  .strict();

export const startBatchRunRequestSchema = z
  .object({
    installId: extensionInstallIdSchema,
    extensionVersion: extensionVersionSchema,
    startRequestId: z.string().uuid(),
  })
  .strict();

export const stopBatchRunRequestSchema = z.object({ installId: extensionInstallIdSchema }).strict();
export const readBatchRunRequestSchema = stopBatchRunRequestSchema;

export const applyBatchRunStatusSchema = z.enum([
  "active",
  "stopping",
  "paused",
  "blocked",
  "completed",
]);

export const batchRunSchema = z
  .object({
    id: z.string().min(1),
    batchId: z.string().min(1),
    installId: extensionInstallIdSchema,
    startRequestId: z.string().uuid(),
    status: applyBatchRunStatusSchema,
    blockedReasonCode: nullableTextSchema,
    startedAt: z.string().datetime(),
    stopRequestedAt: z.string().datetime().nullable(),
    leaseExpiresAt: z.string().datetime(),
    lastHeartbeatAt: z.string().datetime().nullable(),
    replayed: z.boolean(),
  })
  .strict();

export const uncertaintyResolutionSchema = z.enum(["mark_applied", "not_submitted_retry", "skip"]);
export const resolveUncertainRequestSchema = z
  .object({ resolution: uncertaintyResolutionSchema })
  .strict();
export const resolveUncertainResponseSchema = z
  .object({
    applicationId: z.string().min(1),
    applicationStatus: z.string().min(1),
    batchId: nullableIdSchema,
    batchStatus: applyBatchStatusSchema.nullable(),
  })
  .strict();

export type BulkAction = z.infer<typeof bulkActionSchema>;
export type BulkPreflightRequest = z.infer<typeof bulkPreflightRequestSchema>;
export type BulkCommitRequest = z.infer<typeof bulkCommitRequestSchema>;
export type BulkPreflightResponse = z.infer<typeof bulkPreflightResponseSchema>;
export type BulkCommitResponse = z.infer<typeof bulkCommitResponseSchema>;
export type BatchSummary = z.infer<typeof batchSummarySchema>;
export type BatchDetail = z.infer<typeof batchDetailSchema>;
export type BatchRun = z.infer<typeof batchRunSchema>;
export type ResolveUncertainRequest = z.infer<typeof resolveUncertainRequestSchema>;
export type ResolveUncertainResponse = z.infer<typeof resolveUncertainResponseSchema>;
